import { useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { ReactReduxContext } from "react-redux";
import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import type { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { candidateDocumentsApi } from "../store/api/candidateDocumentsApi";
import { isRouteMissing, noteRouteMissing } from "../store/api/routeMissing";
import { getInstanceIdFromUrl } from "../utils/instanceId";
import { sharepointApi } from "../store/api/sharepointApi";
import {
  answeringStore,
  statusFailureIsTemporary,
  useSharePointConnected,
} from "./useSharePointConnected";

/**
 * How often the count is asked for, and when. Exactly the Inbox badge's
 * (useInboxUnreadCount in views/dashboard/sidebar/useShellNavSections.ts):
 * every 30 seconds while the tab is in front, not at all while it is hidden,
 * and once on coming back to it. The interval is fixed: a failed or slow
 * count does not bring the next one forward, and one that is still out is
 * not asked for again.
 */
export const NEW_SUBMISSIONS_POLL: SubscriptionOptions = {
  pollingInterval: 30_000,
  refetchOnFocus: true,
  skipPollingIfUnfocused: true,
};

const noSubscription = () => () => {};

/**
 * Why the count is no longer asked for, for the rest of the page load:
 *
 *   missing  the server has no such route yet
 *   refused  the server said no: the role lost candidate documents, or the
 *            feature was switched off for the instance, while the tab was open
 *
 * Either is an answer, and the poll would only hear it again every 30
 * seconds for as long as the tab stays open: ten people leaving the app open
 * overnight would be thousands of refused requests, each a line in the
 * server's log. A failure that may pass (no connection, a 5xx, a rate limit)
 * is not an answer, and the poll goes on.
 */
type Stopped = "missing" | "refused";
const stoppedFor = new Map<string, Stopped>();

/** For tests: forget which instances the count stopped being asked for. */
export function forgetMissingCountRoutes(): void {
  stoppedFor.clear();
}

/**
 * How many candidates have submitted documents that nobody has opened yet,
 * for the badge on the Document Centre's sidebar item.
 *
 * Asked only where the Document Centre is there for this person (the same
 * answer the sidebar item itself goes by), so nobody else's browser asks
 * anything. Zero until it is known, zero when it cannot be read, and zero
 * with no badge on a server that does not have the count yet or that refuses
 * it. After either of those it is not asked for again until the page is
 * loaded again.
 *
 * Written against the store directly and not with the generated query hook,
 * for the reason useSharePointConnected is: the sidebar is drawn in places
 * with no store, and with a store that does not carry the API slice, and a
 * generated hook throws in both. This one asks nothing there and answers 0.
 */
export function useNewDocumentSubmissions(): number {
  // From the address, as the rest of the shell's navigation reads it.
  const instanceId = getInstanceIdFromUrl();
  const store = answeringStore(useContext(ReactReduxContext)?.store);
  const { connected } = useSharePointConnected(instanceId);

  const select = useMemo(
    () =>
      candidateDocumentsApi.endpoints.getNewSubmissionsCount.select({
        instanceId: instanceId ?? "",
      }),
    [instanceId],
  );

  // Whether the server gave an answer that ends the asking. Read from the
  // cache entry, and remembered for the page load once seen. A string, so the
  // snapshot is the same value between draws unless the answer changed.
  const stopped = useSyncExternalStore<Stopped | "">(
    store && instanceId ? store.subscribe : noSubscription,
    () => {
      if (!store || !instanceId) return "";
      const known = stoppedFor.get(instanceId);
      if (known) return known;
      try {
        const entry = select(store.getState() as Parameters<typeof select>[0]);
        if (entry.status !== "rejected") return "";
        if (isRouteMissing(entry.error)) return "missing";
        return statusFailureIsTemporary(entry.error) ? "" : "refused";
      } catch {
        return "";
      }
    },
  );
  useEffect(() => {
    if (!stopped || !instanceId || stoppedFor.has(instanceId)) return;
    stoppedFor.set(instanceId, stopped);
    if (stopped === "missing") {
      noteRouteMissing("GET /candidate-documents/new-submissions/count");
      return;
    }
    // Refused: the status this goes by was read before whatever changed. Read
    // it again, once, so the sidebar item itself follows the new answer.
    if (!store) return;
    try {
      const dispatch = store.dispatch as ThunkDispatch<unknown, unknown, UnknownAction>;
      const read = dispatch(
        sharepointApi.endpoints.getSharePointStatus.initiate(
          { instanceId },
          { forceRefetch: true },
        ),
      );
      void Promise.resolve(read)
        .catch(() => {})
        .finally(() => read.unsubscribe());
    } catch {
      // The count is no longer asked for either way.
    }
  }, [stopped, instanceId, store]);

  const asking = Boolean(store && instanceId && connected && !stopped);
  useEffect(() => {
    if (!store || !instanceId || !asking) return undefined;
    try {
      const dispatch = store.dispatch as ThunkDispatch<unknown, unknown, UnknownAction>;
      const held = dispatch(
        candidateDocumentsApi.endpoints.getNewSubmissionsCount.initiate(
          { instanceId },
          { subscriptionOptions: NEW_SUBMISSIONS_POLL },
        ),
      );
      // Letting go ends the poll: nothing is asked for a sidebar that is gone.
      return () => held.unsubscribe();
    } catch {
      // A store that cannot run the request leaves the count at zero.
      return undefined;
    }
  }, [store, instanceId, asking]);

  return useSyncExternalStore<number>(store && asking ? store.subscribe : noSubscription, () => {
    if (!store || !asking) return 0;
    try {
      const count = select(store.getState() as Parameters<typeof select>[0]).data?.count;
      return typeof count === "number" && count > 0 ? count : 0;
    } catch {
      return 0;
    }
  });
}
